import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "./axiosConfig";
import { motion, AnimatePresence } from "framer-motion";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const statusTone = {
  PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  RUNNING: "bg-[#edf4ff] text-[#0f62fe] dark:bg-blue-900/30 dark:text-blue-300",
  COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  COMPLETED_WITH_ERRORS: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
  FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
};

function PublicacionesRefreshJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get("/api/publicaciones/refresh-jobs", { params: { limit: 25 } });
      const items = Array.isArray(response.data?.jobs) ? response.data.jobs : [];
      setJobs(items);
      if (selectedJob?.job_id) {
        const nextSelected = items.find((job) => job.job_id === selectedJob.job_id);
        if (nextSelected) setSelectedJob(nextSelected);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || ("Error cargando jobs: " + err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedJob?.job_id]);

  useEffect(() => {
    fetchJobs();
    const intervalId = setInterval(fetchJobs, 5000);
    return () => clearInterval(intervalId);
  }, [fetchJobs]);

  const stats = useMemo(() => {
    const total = jobs.length;
    const running = jobs.filter((job) => ["PENDING", "RUNNING"].includes(job.status)).length;
    const completed = jobs.filter((job) => job.status === "COMPLETED").length;
    const withErrors = jobs.filter((job) => ["COMPLETED_WITH_ERRORS", "FAILED"].includes(job.status)).length;
    return { total, running, completed, withErrors };
  }, [jobs]);

  return (
    <div className="sigc-shell flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
      <motion.section
        className="sigc-card rounded-[28px] p-6 md:p-8"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-2">
            <span className="sigc-eyebrow">Supervision tecnica</span>
            <h1 className="sigc-title">Jobs de actualizacion de publicaciones</h1>
            <p className="sigc-lead">
              Revisa los procesos completados, los que siguen en ejecucion y cualquier fallo
              detectado durante la sincronizacion masiva con OpenAlex.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchJobs}
            className="sigc-button-secondary inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold"
          >
            <span className="material-icons-round mr-2 text-sm">refresh</span>
            Actualizar panel
          </button>
        </div>
      </motion.section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Jobs registrados", value: stats.total, icon: "inventory_2" },
          { label: "En proceso", value: stats.running, icon: "sync" },
          { label: "Completados", value: stats.completed, icon: "check_circle" },
          { label: "Con observaciones", value: stats.withErrors, icon: "warning" },
        ].map((item) => (
          <motion.div
            key={item.label}
            className="sigc-card rounded-2xl p-5"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{item.label}</p>
                <p className="mt-3 text-3xl font-semibold text-slate-950 dark:text-white">{item.value}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#edf4ff] text-[#0f62fe] dark:bg-blue-900/30 dark:text-blue-300">
                <span className="material-icons-round">{item.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      <AnimatePresence>
        {error && (
          <motion.div
            className="rounded-2xl border border-rose-200 bg-rose-50 p-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="flex items-start">
              <span className="material-icons-round mr-3 text-rose-500">error</span>
              <div>
                <p className="text-sm font-semibold text-rose-800">No se pudo cargar el historial</p>
                <p className="mt-1 text-sm text-rose-700">{error}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
        <motion.div
          className="sigc-card overflow-hidden rounded-[28px] p-0"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
        >
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/80 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/50">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">Historial reciente</p>
              <p className="mt-1 text-xs text-slate-500">Los jobs se refrescan automaticamente cada 5 segundos.</p>
            </div>
            <span className="sigc-chip">{jobs.length} jobs</span>
          </div>

          {loading ? (
            <div className="px-6 py-14 text-center text-sm text-slate-500">Cargando historial...</div>
          ) : jobs.length === 0 ? (
            <div className="px-6 py-14 text-center text-sm text-slate-500">Todavia no hay jobs registrados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="bg-slate-100 dark:bg-slate-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Creado</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Progreso</th>
                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Resultado</th>
                    <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Accion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-900/30">
                  {jobs.map((job) => (
                    <tr key={job.job_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {job.created_at ? new Date(job.created_at).toLocaleString() : "-"}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone[job.status] || "bg-slate-100 text-slate-700"}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                        {job.processed_count || 0} / {job.total_candidates || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                        {job.updated_count || 0} actualizadas, {job.error_count || 0} errores
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedJob(job)}
                          className="inline-flex items-center rounded-xl bg-[#edf4ff] px-3 py-2 text-xs font-semibold text-[#0f62fe] transition-colors hover:bg-[#dbeafe]"
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        <motion.aside
          className="sigc-card rounded-[28px] p-6"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
        >
          {!selectedJob ? (
            <div className="flex h-full min-h-[280px] flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#edf4ff] text-[#0f62fe]">
                <span className="material-icons-round">travel_explore</span>
              </div>
              <p className="mt-4 text-base font-semibold text-slate-900 dark:text-white">Selecciona un job</p>
              <p className="mt-2 max-w-sm text-sm text-slate-500">
                Aqui veras filtros, conteos, errores y trazabilidad del proceso escogido.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Detalle del job</p>
                  <p className="mt-1 text-xs text-slate-500 break-all">{selectedJob.job_id}</p>
                </div>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone[selectedJob.status] || "bg-slate-100 text-slate-700"}`}>
                  {selectedJob.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Procesadas</p>
                  <p className="mt-2 text-xl font-semibold text-slate-950 dark:text-white">{selectedJob.processed_count || 0}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Actualizadas</p>
                  <p className="mt-2 text-xl font-semibold text-slate-950 dark:text-white">{selectedJob.updated_count || 0}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Omitidas</p>
                  <p className="mt-2 text-xl font-semibold text-slate-950 dark:text-white">{selectedJob.skipped_count || 0}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                  <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Errores</p>
                  <p className="mt-2 text-xl font-semibold text-slate-950 dark:text-white">{selectedJob.error_count || 0}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/40">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Ventana de ejecucion</p>
                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  <p><span className="font-medium text-slate-800 dark:text-white">Inicio:</span> {selectedJob.started_at ? new Date(selectedJob.started_at).toLocaleString() : "-"}</p>
                  <p><span className="font-medium text-slate-800 dark:text-white">Fin:</span> {selectedJob.finished_at ? new Date(selectedJob.finished_at).toLocaleString() : "-"}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/40">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Errores registrados</p>
                {Array.isArray(selectedJob.errors) && selectedJob.errors.length > 0 ? (
                  <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                    {selectedJob.errors.map((errorText, index) => (
                      <li key={`${selectedJob.job_id}-detail-${index}`} className="rounded-xl bg-rose-50 px-3 py-2 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                        {errorText}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">Este job no reporta errores.</p>
                )}
              </div>
            </div>
          )}
        </motion.aside>
      </section>
    </div>
  );
}

export default PublicacionesRefreshJobs;
